import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore"; // Firestore imports
import { db } from "../config/Firebase"; // Import your Firebase configuration
import { useNavigate } from "react-router-dom"; // Import useNavigate

// Carousel Component (same style as provided)
function Carousel({ images }) {
  const validImages = images.filter((image) => image);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (validImages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % validImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [validImages]);

  if (validImages.length === 0) {
    return <p className="text-gray-400 italic">No images available</p>;
  }

  return (
    <div className="w-full h-64 relative overflow-hidden">
      {validImages.map((image, index) => (
        <img
          key={index}
          src={image}
          alt="Product"
          loading="lazy"
          className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-500 ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
}

export default function Accessories() {
  const [products, setProducts] = useState([]); // State for products
  const [loading, setLoading] = useState(true); // State for loading
  const [error, setError] = useState(null); // State for errors
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const accessoriesRef = collection(db, "Accessories"); // Reference to the 'Accessories' collection
        const querySnapshot = await getDocs(accessoriesRef);
        const fetchedProducts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(fetchedProducts);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return <p className="text-center text-gray-600">Loading products...</p>;
  }
  if (error) {
    return (
      <p className="text-center text-red-500">Error loading products: {error}</p>
    );
  }
  if (products.length === 0) {
    return (
      <p className="text-center text-gray-500">
        No products found in the "Accessories" collection.
      </p>
    );
  }

  // Function to handle product click and navigate to ProductDetails page
  const handleProductClick = (product) => {
    navigate("/product-details", { state: product });
  };

  // Fixed discount rate for every product
  const DISCOUNT_RATE = 10; // 10%

  return (
    <div className="w-full mx-auto px-6 py-8 sm:px-8 md:px-12">
      <style>
        {`
          @keyframes marquee {
            0%   { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
          .animate-marquee {
            display: inline-block;
            white-space: nowrap;
            animation: marquee 10s linear infinite;
          }
        `}
      </style>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl Rubik-Bold text-black px-2">ACCESSORIES</h1>
        <p
          className="px-3 btn-b bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent cursor-pointer shadow-sm hover:bg-gray-200 hover:scale-110 transition transform duration-200"
          onClick={() => navigate("/accessories")}
        >
          ViewAll
        </p>
      </div>
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {products.map((product) => {
          const newCost = parseFloat(product.Cost) || 0;
          const oldCost = (newCost / (1 - DISCOUNT_RATE / 100)).toFixed(0);
          return (
            <div
              key={product.id}
              className="bg-white rounded-lg shadow-lg flex-shrink-0 basis-1/4 aspect-square transition-transform transform hover:scale-105"
              onClick={() => handleProductClick(product)}
            >
              <Carousel
                images={[
                  product.ImageUrl1,
                  product.ImageUrl2,
                  product.ImageUrl3,
                  product.ImageUrl4,
                ]}
              />
              <div className="mx-4 my-2">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 rounded-full py-2 px-4 shadow-lg overflow-hidden">
                  <div className="animate-marquee">
                    <span className="text-white text-xl font-bold">
                      Free Shipping • Free Shipping • Free Shipping
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-5">
                {newCost > 0 && (
                  <div className="Rubik-Bold text-xl text-gray-800 mt-2">
                    <span className="text-black text-2xl mr-2">
                      Price: ₹{newCost.toFixed(0)}
                    </span>
                    <span className="text-gray-400 line-through mr-2 text-xl">
                      ₹{oldCost}
                    </span>
                    <span className="text-cyan-600 text-xl">
                      ({DISCOUNT_RATE}% OFF)
                    </span>
                  </div>
                )}
                <div className="flex justify-between mt-2">
                  <p className="Rubik-Medium text-gray-800">
                    {product.Name || "N/A"}
                  </p>
                  <p className="text-gray-500 Rubik-Light">
                    PID: {product.ProductID || "N/A"}
                  </p>
                </div>
                <p className="text-black Rubik-Light">
                  <span className="font-semibold">Frame Color:</span>{" "}
                  <span className="text-gray-600">
                    {product.FrameColor || "Unknown"}
                  </span>
                </p>
                <p className="text-black Rubik-Light">
                  <span className="font-semibold">Material:</span>{" "}
                  <span className="text-gray-600">
                    {product.Material || "Unknown"}
                  </span>
                </p>
                <p
                  className={`font-semibold flex items-center ${
                    product.Status === "Available"
                      ? "text-green-500"
                      : product.Status === "Sold"
                      ? "text-red-500"
                      : "text-gray-500"
                  }`}
                >
                  {product.Status === "Available" && (
                    <span className="inline-flex items-center justify-center rounded-full bg-green-100 p-1 mr-1">
                      <svg
                        className="w-4 h-4 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                  )}
                  {product.Status || "Unknown"}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
